import React, { useEffect, useState } from 'react';
import { feedbackAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import HLSPlayer from '../../components/HLSPlayer';
import { motion } from 'framer-motion';
import { Video, Clock, MessageSquare, Loader2, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CoachFeedbackReview() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState({});

  useEffect(() => { loadFeedbacks(); }, []);

  const loadFeedbacks = async () => {
    try {
      const res = await feedbackAPI.list();
      setFeedbacks(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleDownload = async (videoFilename, playerName, createdAt) => {
    const token = localStorage.getItem('elev802_token');
    const safeName = (playerName || 'player').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const dateStr = createdAt ? new Date(createdAt).toISOString().slice(0, 10) : 'video';
    const desiredFilename = `feedback_${dateStr}_${safeName}.mp4`;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const toastId = toast.loading('Preparing download...');
    try {
      const safeKey = videoFilename.split('/').map(encodeURIComponent).join('/');
      if (isMobile) {
        // Mobile (iOS PWA can't do blob downloads): get a short-lived signed link
        // and let the browser handle the native save via Content-Disposition.
        const linkResp = await fetch(`${API_URL}/api/admin/download-link/${safeKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!linkResp.ok) throw new Error(`HTTP ${linkResp.status}`);
        const { url } = await linkResp.json();
        toast.success('Opening download...', { id: toastId });
        window.location.href = `${API_URL}${url}`;
        return;
      }
      // Desktop: keep the blob path so the filename is preserved
      const resp = await fetch(`${API_URL}/api/admin/download-video/${safeKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = desiredFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download ready', { id: toastId });
    } catch {
      toast.error('Download failed', { id: toastId });
    }
  };

  const handleReview = async (id) => {
    const text = responses[id];
    if (!text?.trim()) { toast.error('Enter your feedback'); return; }
    setSubmitting(s => ({ ...s, [id]: true }));    try {
      await feedbackAPI.review(id, { coach_response: text });
      toast.success('Feedback sent');
      setResponses(r => ({ ...r, [id]: '' }));
      loadFeedbacks();
    } catch { toast.error('Failed'); }
    setSubmitting(s => ({ ...s, [id]: false }));
  };

  const pending = feedbacks.filter(f => f.status === 'pending');
  const reviewed = feedbacks.filter(f => f.status === 'reviewed');

  return (
    <Layout title="REVIEWS" showBack>
      <div className="px-4 py-6 space-y-6">
        <div>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight" data-testid="reviews-title">PLAYER REVIEWS</h2>
          <p className="text-xs text-[#64748B] mt-1">{pending.length} pending &middot; {reviewed.length} reviewed</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : feedbacks.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
            <p className="text-[#64748B] text-sm">No submissions yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pending First */}
            {pending.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} className="text-[#FF9100]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-[#FF9100]">Pending Review</span>
                </div>
                {pending.map((fb, i) => (
                  <motion.div
                    key={fb.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white border border-[#FF9100]/30 rounded-xl p-4 space-y-3"
                    data-testid={`pending-feedback-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-['Barlow_Condensed'] text-sm font-bold">{fb.user_name}</span>
                      <div className="flex items-center gap-3">
                        {fb.video_filename && (
                          <button
                            onClick={() => handleDownload(fb.video_filename, fb.user_name, fb.created_at)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                            title="Download video"
                            data-testid={`download-pending-${i}`}
                          >
                            <Download size={11} /> Download
                          </button>
                        )}
                        <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                          <Clock size={10} /> {new Date(fb.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {fb.video_filename && (
                      <HLSPlayer
                        videoFilename={fb.video_filename}
                        className="w-full rounded-lg"
                      />
                    )}

                    {fb.notes && <p className="text-sm text-[#64748B]">{fb.notes}</p>}

                    <textarea
                      value={responses[fb.id] || ''}
                      onChange={e => setResponses(r => ({ ...r, [fb.id]: e.target.value }))}
                      className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#64748B] resize-none h-20"
                      placeholder="Your coaching feedback..."
                      data-testid={`feedback-response-${i}`}
                    />

                    <button
                      onClick={() => handleReview(fb.id)}
                      disabled={submitting[fb.id]}
                      className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                      data-testid={`send-feedback-${i}`}
                    >
                      {submitting[fb.id] ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                      {submitting[fb.id] ? 'Sending...' : 'Send Feedback'}
                    </button>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Reviewed */}
            {reviewed.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#10B981]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-[#10B981]">Reviewed</span>
                </div>
                {reviewed.map((fb, i) => (
                  <div key={fb.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid={`reviewed-feedback-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-['Barlow_Condensed'] text-sm font-bold">{fb.user_name}</span>
                      <div className="flex items-center gap-3">
                        {fb.video_filename && (
                          <button
                            onClick={() => handleDownload(fb.video_filename, fb.user_name, fb.created_at)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                            title="Download video"
                            data-testid={`download-reviewed-${i}`}
                          >
                            <Download size={11} /> Download
                          </button>
                        )}
                        <span className="text-[10px] text-[#64748B]">{new Date(fb.reviewed_at || fb.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {fb.video_filename && (
                      <HLSPlayer
                        videoFilename={fb.video_filename}
                        className="w-full rounded-lg"
                      />
                    )}
                    {fb.notes && <p className="text-xs text-[#64748B]">{fb.notes}</p>}
                    <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0]">
                      <div className="flex items-center gap-1 mb-1">
                        <MessageSquare size={10} className="text-[#00E5FF]" />
                        <span className="text-[9px] text-[#00E5FF] font-bold uppercase tracking-widest">Your Response</span>
                      </div>
                      <p className="text-xs text-[#64748B]">{fb.coach_response}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
